"use client";

import SensayLogo from "@/public/sensay.svg";
import { ArrowCircleRight, YoutubeLogo } from "@phosphor-icons/react/dist/ssr";
import { LinkedinLogo, MediumLogo, TelegramLogo, XLogo } from "@phosphor-icons/react/dist/ssr";
import Image from "next/image";
import Link from "next/link";
import useSubscribe from "../hooks/useSubscribe";
import ButtonV2 from "./ButtonV2";

const socials = [
  {
    href: "https://t.me/kolzchat",
    icon: TelegramLogo,
  },
  {
    href: "https://www.linkedin.com/company/kolz",
    icon: LinkedinLogo,
  },
  {
    href: "https://x.com/ChatKolz",
    icon: XLogo,
  },
  {
    href: "https://kolz.medium.com",
    icon: MediumLogo,
  },
  {
    href: "https://www.youtube.com/@KOLZchat",
    icon: YoutubeLogo,
  },
];

function Socials() {
  return (
    <ul className="flex space-x-1 lg:space-x-3 text-black">
      {socials.map((social) => (
        <li key={social.href}>
          <a href={social.href} target="_about" className="p-1 bg-white rounded-full block">
            <social.icon className="h-6 w-6" />
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function Footer() {
  return (
    <footer className="border-t-[#2E2E2E] border-t-[1px] space-x-2 bg-[#101010] text-white lg:mx-0">
      <div className="flex justify-between items-start lg:flex-row flex-col px-6 py-9">
        <div className="flex flex-col lg:block w-full lg:w-auto">
          <div>
            <Image src="/v2/kolz.svg" alt="kolz-logo" height={96} width={28} className="w-24 h-7 mb-3" />
            <div className="flex items-center font-inter">
              <span>Powered&nbsp;by</span>
              <Link
                href="https://sensay.io"
                className="hover:underline inline-block ml-2 -mb-1 hover:[&_svg]:fill-[#a3426c]"
                target="_blank"
              >
                <SensayLogo className="h-[20px] shrink-0 fill-current transition-colors duration-200" />
              </Link>
            </div>
          </div>
          <div className="mt-5">
            <Socials />
          </div>
        </div>
        <SubscriptionForm className="flex lg:hidden w-full max-w-72 mt-12 justify-between flex-col lg:space-y-2" />
        <div className="flex w-full justify-between lg:justify-evenly lg:mt-0 mt-4">
          <div className="lg:flex flex-col space-y-10">
            <nav className="grid grid-cols-2 gap-6">
              <ul className="font-inter">
                <li className="rounded-full  px-2">
                  <Link href="/#about">About</Link>
                </li>
                <li className="rounded-full  px-2">
                  <Link href="/pricing">Pricing</Link>
                </li>
                <li className="rounded-full  px-2">
                  <Link href="/team">Team</Link>
                </li>
                <li className="rounded-full  px-2">
                  <Link href="/sign-up" className="text-[#43BEA9] font-semibold hover:text-[#38A08E] transition-colors">
                    Sign Up
                  </Link>
                </li>
              </ul>
              <ul>
                <li className="rounded-full  px-2">
                  <Link href="https://docs.kolz.chat" rel="noreferrer noopener" target="_blank">
                    Docs
                  </Link>
                </li>
                <li className="rounded-full  px-2">
                  <Link href="/for-influencers">For Influencers</Link>
                </li>
                <li className="rounded-full  px-2">
                  <Link href="/privacy">Privacy</Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div>
          <SubscriptionForm className="hidden lg:flex justify-between flex-col lg:space-y-2 w-[200px]" />
          <p className="ml-2 mt-6">
            Audited by{" "}
            <Link
              href="https://app.solidproof.io/projects/kolz"
              rel="noreferrer noopener"
              target="_blank"
              className="underline"
            >
              SolidProof
            </Link>
          </p>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-6 justify-between lg:items-end p-6 border-t-[#2E2E2E] border-t-[1px]">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col-reverse xl:items-center gap-3 xl:flex-row">
            <div className="flex items-center flex-wrap gap-3">
              <span className="font-medium">$KOLZ&nbsp;token:</span>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://coinmarketcap.com/currencies/kolz/"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#2E2E2E] hover:bg-[#3E3E3E] px-4 py-1 rounded-full text-sm"
                >
                  CoinMarketCap
                </a>
                <a
                  href="https://dexscreener.com/base/0x50CE4129Ca261CCDe4EB100c170843c2936Bc11b"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#2E2E2E] hover:bg-[#3E3E3E] px-4 py-1 rounded-full text-sm"
                >
                  DexScreener
                </a>
                <a
                  href="https://www.coingecko.com/en/coins/kolz"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-[#2E2E2E] hover:bg-[#3E3E3E] px-4 py-1 rounded-full text-sm"
                >
                  CoinGecko
                </a>
              </div>
            </div>
            <div className="hidden xl:block h-8 w-px bg-gradient-to-b from-transparent via-gray-500 to-transparent" />
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <a href="https://www.mexc.com/exchange/KOLZ_USDT" target="_blank" rel="noreferrer">
                <ButtonV2 color="secondary">Get $KOLZ</ButtonV2>
              </a>
              <a href="https://staking.kolz.chat" target="_blank" rel="noreferrer">
                <ButtonV2 color="secondary">Staking $KOLZ</ButtonV2>
              </a>
            </div>
          </div>
          <div className="text-sm space-y-2">
            <p>
              <span className="font-bold">KOLZ Base Token: </span>
              <span className="font-mono text-xs text-[#97B1FF]">0x50CE4129Ca261CCDe4EB100c170843c2936Bc11b</span>
            </p>
            <p>
              <span className="font-bold">KOLZ Token Ethereum Network: </span>
              <span className="font-mono text-xs text-[#97B1FF]">0x288741b45AD4042f7b124E38b53cEc5E9ccA0376</span>
            </p>
          </div>
        </div>

        <div className="text-center lg:text-right">
          <span className="font-semibold text-lg">
            © {new Date().getFullYear()} Kolz <br />
          </span>
          <span className="text-xs">KOLZ Pte Ltd. Suntec Tower 2, 117, 8 Temasek Blvd, Singapore 038988</span>
        </div>
      </div>
    </footer>
  );
}

const SubscriptionForm = ({ className }: { className?: string }) => {
  const { email, setEmail, handleSubmit, isLoading } = useSubscribe();

  return (
    <form onSubmit={(e) => handleSubmit(e)} className={className}>
      <span className="font-inter font-medium text-sm w-full mb-3 lg:mb-0">Subscribe to our newsletter</span>
      <div className="flex text-white w-full">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          className="bg-[#2E2E2E] font-inter border-black border p-2 rounded-lg w-full"
          placeholder="Email address"
          required
          disabled={isLoading}
        />
        <button type="submit" className="disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading}>
          <ArrowCircleRight className="w-8 h-8" />
        </button>
      </div>
    </form>
  );
};
